package net.bothosting.docs

import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.graphics.lightened
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    ctx.stylesheet.registerStyle("html") {
        base {
            Modifier
                .fontFamily("DM Sans", "Inter", "system-ui", "sans-serif")
        }
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }

    ctx.stylesheet.registerStyle(".content-area") {
        // ── Headings ──────────────────────────────────────────────
        cssRule(" h1, h2, h3, h4") {
            Modifier
                .position(Position.Relative)
        }
        cssRule(" h1") {
            Modifier
                .fontSize(2.5.cssRem)
                .margin(top = 2.cssRem, bottom = 1.cssRem)
                .fontWeight(FontWeight.Bold)
                .lineHeight(1.2)
        }
        cssRule(" h2") {
            Modifier
                .fontSize(1.75.cssRem)
                .margin(top = 2.cssRem, bottom = 0.75.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .lineHeight(1.3)
                .padding(bottom = 0.5.cssRem)
                .borderBottom(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
        }
        cssRule(" h3") {
            Modifier
                .fontSize(1.3.cssRem)
                .margin(top = 1.5.cssRem, bottom = 0.5.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .lineHeight(1.4)
        }
        cssRule(" h4") {
            Modifier
                .fontSize(1.1.cssRem)
                .margin(top = 1.25.cssRem, bottom = 0.5.cssRem)
                .fontWeight(FontWeight.Medium)
                .lineHeight(1.4)
        }
        cssRule(" .header-anchor") {
            Modifier.color(Color.rgba(255, 255, 255, 0.0f))
        }
        cssRule(" h1:hover > .header-anchor, h2:hover > .header-anchor, h3:hover > .header-anchor, h4:hover > .header-anchor") {
            Modifier.color(Color.rgba(255, 255, 255, 0.4f))
        }
        cssRule(" h1 > .header-anchor:hover, h2 > .header-anchor:hover, h3 > .header-anchor:hover, h4 > .header-anchor:hover") {
            Modifier.color(Color.rgb(0x3b9eff))
        }

        // ── Paragraphs ────────────────────────────────────────────
        cssRule(" p") {
            Modifier
                .margin(top = 0.px, bottom = 1.cssRem)
                .lineHeight(1.75)
                .color(Color.rgba(255, 255, 255, 0.85f))
        }

        // ── Lists ─────────────────────────────────────────────────
        cssRule(" ul, ol") {
            Modifier
                .margin(top = 0.px, bottom = 1.cssRem)
                .padding(left = 1.5.cssRem)
        }
        cssRule(" li") {
            Modifier
                .margin(bottom = 0.4.cssRem)
                .lineHeight(1.65)
                .color(Color.rgba(255, 255, 255, 0.85f))
        }
        cssRule(" li > p") {
            Modifier.margin(bottom = 0.px)
        }

        // ── Inline code ───────────────────────────────────────────
        cssRule(" code") {
            Modifier
                .backgroundColor(Color.rgba(30, 41, 59, 0.7f))
                .padding(0.2.cssRem, 0.4.cssRem)
                .borderRadius(4.px)
                .fontSize(0.875.cssRem)
                .fontFamily("ui-monospace", "SFMono-Regular", "Menlo", "Monaco", "Consolas", "Liberation Mono", "Courier New", "monospace")
                .color(Color.rgb(0xe2e8f0))
        }

        // ── Code blocks ───────────────────────────────────────────
        cssRule(" pre") {
            Modifier
                .backgroundColor(Color.rgb(0x0a0f1a))
                .padding(1.25.cssRem)
                .borderRadius(8.px)
                .margin(topBottom = 1.5.cssRem)
                .overflow { x(Overflow.Auto) }
                .fillMaxWidth()
                .border(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.06f))
        }
        cssRule(" pre > code") {
            Modifier
                .backgroundColor(Colors.Transparent)
                .padding(0.px)
                .borderRadius(0.px)
                .fontSize(0.85.cssRem)
                .lineHeight(1.6)
                .color(Color.rgb(0xe2e8f0))
        }

        // ── Links ─────────────────────────────────────────────────
        cssRule(" a") {
            Modifier
                .color(Color.rgb(0x3b9eff))
                .textDecorationLine(TextDecorationLine.None)
                .fontWeight(FontWeight.Medium)
        }
        cssRule(" a:hover") {
            Modifier
                .textDecorationLine(TextDecorationLine.Underline)
                .color(Color.rgb(0x6ab0ff))
        }

        // ── Horizontal rule ───────────────────────────────────────
        cssRule(" hr") {
            Modifier
                .margin(topBottom = 2.cssRem)
                .borderTop(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.08f))
                .fillMaxWidth()
        }

        // ── Blockquotes ───────────────────────────────────────────
        cssRule(" blockquote") {
            Modifier
                .margin(topBottom = 1.5.cssRem, leftRight = 0.px)
                .padding(top = 0.5.cssRem, bottom = 0.5.cssRem, left = 1.25.cssRem, right = 1.cssRem)
                .borderLeft(4.px, LineStyle.Solid, Color.rgba(59, 158, 255, 0.5f))
                .backgroundColor(Color.rgba(59, 158, 255, 0.06f))
                .borderRadius(0.px, 6.px, 6.px, 0.px)
                .color(Color.rgba(255, 255, 255, 0.8f))
        }
        cssRule(" blockquote p") {
            Modifier.margin(bottom = 0.5.cssRem)
        }

        // ── Tables ────────────────────────────────────────────────
        cssRule(" table") {
            Modifier
                .fillMaxWidth()
                .margin(topBottom = 1.5.cssRem)
                .fontSize(0.9.cssRem)
        }
        cssRule(" th") {
            Modifier
                .backgroundColor(Color.rgba(255, 255, 255, 0.06f))
                .padding(0.75.cssRem, 1.cssRem)
                .fontWeight(FontWeight.SemiBold)
                .textAlign(TextAlign.Start)
                .borderBottom(2.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.1f))
                .color(Color.rgba(255, 255, 255, 0.9f))
        }
        cssRule(" td") {
            Modifier
                .padding(0.7.cssRem, 1.cssRem)
                .borderBottom(1.px, LineStyle.Solid, Color.rgba(255, 255, 255, 0.06f))
                .color(Color.rgba(255, 255, 255, 0.85f))
        }
        cssRule(" tr:nth-child(even) td") {
            Modifier.backgroundColor(Color.rgba(255, 255, 255, 0.03f))
        }
        cssRule(" tr:hover td") {
            Modifier.backgroundColor(Color.rgba(255, 255, 255, 0.06f))
        }

        // ── Images ────────────────────────────────────────────────
        cssRule(" img") {
            Modifier
                .maxWidth(100.percent)
                .borderRadius(8.px)
                .margin(topBottom = 1.cssRem)
        }
        cssRule(" figure") {
            Modifier
                .margin(topBottom = 1.5.cssRem, leftRight = 0.px)
                .textAlign(TextAlign.Center)
        }
        cssRule(" figcaption") {
            Modifier
                .fontSize(0.85.cssRem)
                .color(Color.rgba(255, 255, 255, 0.5f))
                .margin(top = 0.5.cssRem)
        }

        // ── Bold / strong ─────────────────────────────────────────
        cssRule(" strong") {
            Modifier.fontWeight(FontWeight.Bold).color(Color.rgba(255, 255, 255, 0.95f))
        }

        // ── Nested list styles ────────────────────────────────────
        cssRule(" ul ul, ol ol, ul ol, ol ul") {
            Modifier.margin(top = 0.4.cssRem, bottom = 0.px)
        }

        // ── Hint / Callout boxes ──────────────────────────────────
        cssRule(" .hint-icon i") {
            Modifier.color(Color.rgb(0x008bf6))
        }
        cssRule(" .hint.hint-info .hint-icon i") {
            Modifier.color(Color.rgb(0x3b9eff))
        }
        cssRule(" .hint.hint-warning .hint-icon i") {
            Modifier.color(Color.rgb(0xf5b800))
        }
        cssRule(" .hint.hint-success .hint-icon i") {
            Modifier.color(Color.rgb(0x10b981))
        }
        cssRule(" .hint.hint-danger .hint-icon i") {
            Modifier.color(Color.rgb(0xef4444))
        }
        cssRule(" .hint-content code") {
            Modifier
                .backgroundColor(Color.rgba(30, 41, 59, 0.5f))
                .padding(0.15.cssRem, 0.35.cssRem)
                .borderRadius(3.px)
                .fontSize(0.85.cssRem)
        }
    }

    // ── Prevent underline on hover for section card links ──────
    ctx.stylesheet.registerStyle(".content-area .section-card-link:hover") {
        base {
            Modifier.textDecorationLine(TextDecorationLine.None)
        }
    }

    // ── Sidebar scrollbar: hidden by default, visible on hover ─
    ctx.stylesheet.registerStyle(".sidebar") {
        base {
            Modifier
                .scrollbarWidth(ScrollbarWidth.None)
        }
        cssRule("::-webkit-scrollbar") {
            Modifier.width(0.px)
        }
    }
    ctx.stylesheet.registerStyle(".sidebar:hover") {
        base {
            Modifier
                .scrollbarWidth(ScrollbarWidth.Auto)
        }
        cssRule("::-webkit-scrollbar") {
            Modifier.width(6.px)
        }
    }
}

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(3.cssRem)
        .textAlign(TextAlign.Start)
        .lineHeight(1.2) //1.5x doesn't look as good on very large text
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(colorMode.toPalette().color.toRgb().copyf(alpha = 0.8f))
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}
